import Component from '@glimmer/component';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { variation } from 'ember-launch-darkly';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import {
  HdsCardContainer,
  HdsButton,
  HdsStepperList,
} from '@hashicorp/design-system-components/components';
import { Typography } from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';

/**
 *
 * `PageHvnsCardsGetStarted` this renders static HVN Get started content. Eventually
 * this will be updated to contain dynamic content.
 *
 *
 * ```
 * <Page::Hvns::Cards::GetStarted @networks={{networks}}} @project={{project}} />
 * ```
 *
 * @class PageHvnsCardsGetStarted
 *
 */
export default class PageHvnsCardsGetStarted extends Component {
  @service analytics;

  captureAnalyticsEventABTestHvnCreateIntentOnBoardingStepper = () => {
    const abTestVariation = variation('hcp-ui-ab-test-hvn-activation-create');

    this.analytics.trackEvent(
      'external_ui_hvn_activation_create_intent_button_clicked',
      { abTestVariation, buttonLocation: 'onboarding-stepper-step-1' }
    );
  };

  <template>
    <HdsCardContainer
      data-test-hvns-cards-get-started
      class="hvns-cards-get-started"
      @overflow="visible"
      @level="mid"
      @hasBorder={{true}}
    >
      <div {{boxPadding "lg lg md lg"}}>
        <Typography
          @variant="h2"
          class="hds-typography-display-400 hds-font-weight-semibold hds-foreground-strong"
          {{boxMargin "0 0 md 0"}}
          data-test-hvns-cards-get-started-title
        >
          {{t "components.page.hvns.cards.get-started.title"}}
        </Typography>
        <HdsStepperList
          @titleTag="h3"
          @ariaLabel={{t "components.page.hvns.cards.get-started.title"}}
          {{boxMargin "0 0 md 0"}}
          as |S|
        >
          <S.Step>
            <:title>{{t
                "components.page.hvns.cards.get-started.step-1.label"
              }}</:title>
            <:description>{{t
                "components.page.hvns.cards.get-started.step-1.content"
              }}</:description>
            <:content>
              {{#if (can "create network" @networks)}}
                <HdsButton
                  @color="primary"
                  @icon="plus"
                  @iconPosition="leading"
                  @route="cloud.orgs.detail.projects.detail.hvns.create"
                  @text={{t "components.page.hvns.create.action"}}
                  data-test-network-create
                  {{on
                    "click"
                    this.captureAnalyticsEventABTestHvnCreateIntentOnBoardingStepper
                  }}
                />
              {{/if}}
            </:content>
          </S.Step>
          <S.Step>
            <:title>{{t
                "components.page.hvns.cards.get-started.step-2.label"
              }}</:title>
            <:description>{{t
                "components.page.hvns.cards.get-started.step-2.content"
              }}</:description>
          </S.Step>
          <S.Step>
            <:title>{{t
                "components.page.hvns.cards.get-started.step-3.label"
              }}</:title>
            <:description>{{t
                "components.page.hvns.cards.get-started.step-3.content"
              }}</:description>
          </S.Step>
        </HdsStepperList>
        <Typography
          @variant="body1"
          class="hds-typography-body-100 hds-foreground-primary"
        >
          {{t
            "components.page.hvns.cards.get-started.completed.content"
            htmlSafe=true
            projectId=@project.id
          }}
        </Typography>
      </div>
    </HdsCardContainer>
  </template>
}
